import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import useUIStore from '../../stores/uiStore';

interface UserInputFieldProps {
  variableName: string;
  placeholder?: string;
  maxLength?: number;
  autoSave?: boolean;
  debounce?: number;
  confirmationTarget?: string;
  className?: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
}

const DEFAULT_DEBOUNCE_MS = 400;
const MIN_DEBOUNCE_MS = 150;
const MAX_DEBOUNCE_MS = 2000;

const clampDebounce = (value: number | undefined) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return DEFAULT_DEBOUNCE_MS;
  }
  return Math.min(MAX_DEBOUNCE_MS, Math.max(MIN_DEBOUNCE_MS, Math.floor(value)));
};

const UserInputField: React.FC<UserInputFieldProps> = ({
  variableName,
  placeholder,
  maxLength,
  autoSave = false,
  debounce,
  confirmationTarget,
  className,
  type = 'text',
  multiline = false,
  rows = 3
}) => {
  const storedValue = useGameStore(
    useCallback(
      (state) => state.userVariableValues[variableName]?.value ?? '',
      [variableName]
    )
  );
  const hasPendingInput = useGameStore(
    useCallback(
      (state) => state.pendingUserInputs[variableName] != null,
      [variableName]
    )
  );
  const currentSceneId = useGameStore((state) => state.currentScene?.id);
  const submitUserInput = useGameStore((state) => state.submitUserInput);
  const setPendingUserInput = useGameStore((state) => state.setPendingUserInput);
  const uiStore = useUIStore();

  const [inputValue, setInputValue] = useState<string>(storedValue ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<number | null>(null);

  const autoSaveEnabled = autoSave === true;
  const effectiveDebounce = useMemo(
    () => (autoSaveEnabled ? clampDebounce(debounce) : undefined),
    [autoSaveEnabled, debounce]
  );

  // When scene changes (navigation), always sync from store so we show the new scene's value
  useEffect(() => {
    setInputValue(storedValue ?? '');
    setDirty(false);
    setError(null);
  }, [currentSceneId]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when scene id changes

  // Keep local state in sync when store value changes from external updates.
  // Preserve local draft only when the store is overwritten with empty AND we still have
  // uncommitted pending input (e.g. partner triggered same-scene refresh). When the store
  // is cleared and we have no pending input (e.g. we clicked Talk and the script cleared
  // the variable), sync and clear the field.
  useEffect(() => {
    if (storedValue !== inputValue) {
      const incomingEmpty = storedValue === '' || storedValue == null;
      const haveLocalDraft = dirty || inputValue !== '';
      if (haveLocalDraft && incomingEmpty && hasPendingInput) {
        return;
      }
      setInputValue(storedValue ?? '');
      setDirty(false);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedValue, hasPendingInput]);

  // Cleanup pending debounce timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  const runSubmission = useCallback(
    async (valueToSubmit: string, { silent }: { silent?: boolean } = {}) => {
      if (!dirty && valueToSubmit === storedValue) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);
        const lengthOptions = maxLength !== undefined ? { maxLength } : undefined;
        await submitUserInput(variableName, valueToSubmit, lengthOptions);
        setDirty(false);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update value';
        setError(message);
        if (!silent) {
          uiStore.setError({
            code: 'USER_INPUT_ERROR',
            message
          });
        }
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [dirty, submitUserInput, variableName, maxLength, uiStore, storedValue]
  );

  // Auto-save behaviour with debounce
  useEffect(() => {
    if (!autoSaveEnabled || !dirty) {
      return;
    }

    if (debounceTimerRef.current !== null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const delay = effectiveDebounce ?? DEFAULT_DEBOUNCE_MS;
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      void runSubmission(inputValue, { silent: true });
    }, delay);

    return () => {
      if (debounceTimerRef.current !== null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [autoSaveEnabled, dirty, effectiveDebounce, inputValue, runSubmission]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setInputValue(value);
      setDirty(true);
      setError(null);

      if (!autoSaveEnabled) {
        const lengthOptions = maxLength !== undefined ? { maxLength } : undefined;
        setPendingUserInput(variableName, value, lengthOptions);
      }
    },
    [autoSaveEnabled, maxLength, setPendingUserInput, variableName]
  );

  const handleBlur = useCallback(() => {
    if (autoSaveEnabled && dirty) {
      void runSubmission(inputValue);
    }
  }, [autoSaveEnabled, dirty, inputValue, runSubmission]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        if (autoSaveEnabled) {
          void runSubmission(inputValue);
        }
      }
    },
    [autoSaveEnabled, inputValue, runSubmission]
  );

  const rootClassName = useMemo(() => {
    const classes = ['rich-text-user-input'];
    if (className) {
      classes.push(className);
    }
    if (isSaving) {
      classes.push('rich-text-user-input--saving');
    }
    if (error) {
      classes.push('rich-text-user-input--error');
    }
    return classes.join(' ');
  }, [className, isSaving, error]);

  const inputClassName = `rich-text-user-input__input${multiline ? ' rich-text-user-input__input--multiline' : ''}`;

  return (
    <div
      className={rootClassName}
      data-variable={variableName}
      data-confirmation-target={confirmationTarget ?? undefined}
    >
      {multiline ? (
        <textarea
          className={inputClassName}
          value={inputValue}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={rows}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label={placeholder ?? variableName}
          disabled={isSaving}
          data-testid={`user-input-${variableName}`}
        />
      ) : (
        <input
          type={type}
          className={inputClassName}
          value={inputValue}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label={placeholder ?? variableName}
          disabled={isSaving}
          data-testid={`user-input-${variableName}`}
        />
      )}
      {isSaving && (
        <span
          className="rich-text-user-input__status"
          aria-live="polite"
          data-testid={`user-input-${variableName}-saving`}
        >
          Saving…
        </span>
      )}
      {error && (
        <span
          className="rich-text-user-input__error-message"
          role="status"
          aria-live="assertive"
          data-testid={`user-input-${variableName}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
};

export default UserInputField;

